import React, { useEffect, useRef } from "react";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";

export type DailyRow = {
  day: string;
  units_ordered: number;
  units_active: number;
  units_cancelled: number;
  revenue_ordered: number;
  revenue_active: number;
};

type Metric = "units_active" | "units_ordered" | "revenue_active";

const metricLabel: Record<Metric, string> = {
  units_active: "Активных ед.",
  units_ordered: "Заказано ед.",
  revenue_active: "Выручка ₽",
};

const DailyBarChart = ({
  days,
  metric = "units_active",
}: {
  days: DailyRow[];
  metric?: Metric;
}) => {
  const idRef = useRef(`fbs-daily-${Math.random().toString(36).slice(2)}`);

  useEffect(() => {
    const root = am5.Root.new(idRef.current);
    root.setThemes([am5themes_Animated.new(root)]);

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: true,
        panY: false,
        wheelX: "panX",
        wheelY: "zoomX",
        layout: root.verticalLayout,
      })
    );

    const xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: "day",
        renderer: am5xy.AxisRendererX.new(root, {
          minGridDistance: 30,
          cellStartLocation: 0.1,
          cellEndLocation: 0.9,
        }),
        tooltip: am5.Tooltip.new(root, {}),
      })
    );
    xAxis.get("renderer").labels.template.setAll({
      rotation: -45,
      centerY: am5.p50,
      centerX: am5.p100,
      fontSize: 11,
    });

    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        min: 0,
        renderer: am5xy.AxisRendererY.new(root, {}),
      })
    );

    const series = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: metricLabel[metric],
        xAxis,
        yAxis,
        valueYField: "value",
        categoryXField: "day",
        tooltip: am5.Tooltip.new(root, {
          labelText: "{categoryX}: {valueY}",
        }),
      })
    );
    series.columns.template.setAll({
      cornerRadiusTL: 3,
      cornerRadiusTR: 3,
      strokeOpacity: 0,
      width: am5.percent(70),
    });

    series.bullets.push(() => {
      return am5.Bullet.new(root, {
        locationY: 1,
        sprite: am5.Label.new(root, {
          text: "{valueY}",
          centerX: am5.p50,
          centerY: am5.p100,
          populateText: true,
          fontSize: 11,
          dy: -4,
        }),
      });
    });

    const data = days.map((d) => ({
      day: d.day.slice(5), // MM-DD for compact labels
      value: Number((d as any)[metric]) || 0,
      fullDay: d.day,
    }));
    xAxis.data.setAll(data);
    series.data.setAll(data);

    chart.set(
      "cursor",
      am5xy.XYCursor.new(root, {
        behavior: "none",
        xAxis,
      })
    );

    return () => root.dispose();
  }, [days, metric]);

  return <div id={idRef.current} style={{ width: "100%", height: 340 }} />;
};

export default DailyBarChart;
